import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import SectionHeading from "@/components/base/SectionHeading";
import EmptyState from "@/components/base/EmptyState";
import SearchBox from "@/components/feature/SearchBox";
import EpisodeCard from "@/components/feature/EpisodeCard";
import PlotCard from "@/components/feature/PlotCard";
import { episodes } from "@/mocks/episodes";
import { actors } from "@/mocks/actors";
import { plots } from "@/mocks/plots";
import { searchAll } from "@/utils/search";

export default function SearchResult() {
  const [params] = useSearchParams();
  const keyword = (params.get("q") || "").trim();

  const results = useMemo(() => (keyword ? searchAll(keyword) : []), [keyword]);

  const episodeResults = episodes.filter((item) =>
    results.some((result) => result.type === "episode" && result.id === item.id),
  );
  const actorResults = actors.filter((item) =>
    results.some((result) => result.type === "actor" && result.id === item.id),
  );
  const plotResults = plots.filter((item) =>
    results.some((result) => result.type === "plot" && result.id === item.id),
  );

  return (
    <>
      <PageMeta
        title={keyword ? `「${keyword}」的搜索结果 - 年轻的母亲1官网` : "搜索 - 年轻的母亲1官网"}
        description={`在年轻的母亲1内容档案站中搜索剧集、演员与剧情解析。${keyword ? `当前搜索关键词：${keyword}。` : ""}`}
        keywords="年轻的母亲1搜索,年轻的母亲1剧集查询,年轻的母亲1演员查询"
      />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-site px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "搜索" }]} />
          <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-3xl">搜索结果</h1>
          {keyword ? (
            <p className="mt-3 text-sm text-foreground-600">
              关键词「<span className="font-medium text-primary-600">{keyword}</span>」共找到{" "}
              <span className="font-semibold text-accent-600">{results.length}</span> 条相关内容
            </p>
          ) : (
            <p className="mt-3 text-sm text-foreground-600">请输入关键词，搜索剧集、演员或剧情解析。</p>
          )}
          <div className="mt-5 max-w-xl">
            <SearchBox />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-site px-4 py-10 md:px-6 md:py-12">
        {!keyword ? (
          <EmptyState
            title="还没有输入搜索关键词"
            description="试试搜索「林晓」「婆婆」或「结局」等关键词，即可在演示数据范围内查看结果。"
            secondaryLabel="返回首页"
            secondaryLink="/"
          />
        ) : results.length === 0 ? (
          <EmptyState
            title={`没有找到与「${keyword}」相关的内容`}
            description="可以尝试更换关键词，或先浏览全部剧集与剧情解析列表。"
            secondaryLabel="返回首页"
            secondaryLink="/"
          />
        ) : (
          <div className="space-y-12">
            {episodeResults.length > 0 ? (
              <section>
                <SectionHeading
                  eyebrow="剧集"
                  title={`相关剧集（${episodeResults.length}）`}
                  moreLink="/category/episodes"
                />
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {episodeResults.map((episode) => (
                    <EpisodeCard key={episode.id} episode={episode} />
                  ))}
                </div>
              </section>
            ) : null}

            {actorResults.length > 0 ? (
              <section>
                <SectionHeading
                  eyebrow="演员"
                  title={`相关演员（${actorResults.length}）`}
                  moreLink="/category/actors"
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {actorResults.map((actor) => (
                    <Link
                      key={actor.id}
                      to={`/detail/actor/${actor.id}`}
                      className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
                    >
                      <span className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                        <img src={actor.avatar} alt={actor.name} className="h-full w-full object-cover object-top" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-foreground-950 group-hover:text-primary-600">
                          {actor.name}
                        </span>
                        <span className="mt-0.5 block text-xs text-foreground-500">饰 {actor.role}</span>
                        <span className="mt-1 line-clamp-1 block text-xs text-foreground-500">{actor.shortBio}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            ) : null}

            {plotResults.length > 0 ? (
              <section>
                <SectionHeading
                  eyebrow="剧情解析"
                  title={`相关剧情解析（${plotResults.length}）`}
                  moreLink="/category/plots"
                />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {plotResults.map((plot) => (
                    <PlotCard key={plot.id} plot={plot} />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}